import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { BudgetsService } from '@/common/integrations/menhir/employee/budgets/budgets.service';
import {
  BudgetsStatus,
  IApplicableBudget,
  IBudgetExpense,
  IBudgetMemberInfo,
} from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { WithAxiosConfig } from '@/common/types/general';
import { defaultMoney } from '@coverflex-tech/hypernova-utils';
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

export function useGetEmployeeBudgetChoices(
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getEmployeeBudgetChoices>>,
    any
  >
) {
  const { data: employeeBudgetChoices, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT, 'choices'],
    queryFn: BudgetsService.getEmployeeBudgetChoices,
    ...options,
  });

  return { employeeBudgetChoices, ...rest };
}

export function useGetBudgets(
  statusIn?: Array<BudgetsStatus>,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getBudgets>>,
    any
  >
) {
  const { data: budgets, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT,
      JSON.stringify(statusIn),
    ],
    queryFn: () => BudgetsService.getBudgets(statusIn),
    ...options,
  });

  return { budgets, ...rest };
}

export function useGetBudgetApplication(
  budgetId: string,
  applicationId: string,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getBudgetApplication>>,
    any
  >
) {
  const { data: budgetApplication, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.DETAIL,
      budgetId,
      applicationId,
    ],
    queryFn: () => BudgetsService.getBudgetApplication(budgetId, applicationId),
    ...options,
  });

  return { budgetApplication, ...rest };
}

export function useGetBudgetApplications(
  budgetId?: string,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getBudgetApplications>>,
    any
  >
) {
  const { data: budgetApplications, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.APPLICATIONS.ROOT, budgetId],
    queryFn: () => BudgetsService.getBudgetApplications(budgetId),
    ...options,
  });

  return { budgetApplications, ...rest };
}

export function useGetBudgetApplicationTemplate(
  budgetId: string,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getBudgetApplicationTemplate>>,
    any
  >
) {
  const { data: budgetApplicationTemplate, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.TEMPLATE,
      budgetId,
    ],
    queryFn: () => BudgetsService.getBudgetApplicationTemplate(budgetId),
    ...options,
  });

  return { budgetApplicationTemplate, ...rest };
}

export function useGetBudgetEmployees(
  budgetId: string,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getBudgetEmployees>>,
    any
  >
) {
  const { data: budgetEmployees, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_EMPLOYEES.ROOT,
      budgetId,
    ],
    queryFn: () => BudgetsService.getBudgetEmployees(budgetId),
    ...options,
  });

  return { budgetEmployees, ...rest };
}

export function useGetBudgetEmployeeDetail(
  budgetId: string,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getBudgetEmployeeDetail>>,
    any
  >
) {
  const { data: budgetEmployeeDetail, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.DETAIL, budgetId],
    queryFn: () => BudgetsService.getBudgetEmployeeDetail(budgetId),
    ...options,
  });

  return { budgetEmployeeDetail, ...rest };
}

export function useGetFamilyMember(
  budgetEmployeeId: string,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof BudgetsService.getFamilyMember>>,
    any
  >
) {
  const { data: familyMember, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.FAMILY_MEMBER,
      budgetEmployeeId,
    ],
    queryFn: () => BudgetsService.getFamilyMember(budgetEmployeeId),
    ...options,
  });

  return { familyMember, ...rest };
}

export function setGetFamilyMemberData(
  budgetEmployeeId: string,
  data: IBudgetMemberInfo
) {
  return queryClient.setQueryData(
    [MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.FAMILY_MEMBER, budgetEmployeeId],
    data
  );
}

export function useSubmitBudgetApplication(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof BudgetsService.submitBudgetApplication>>,
    any,
    Parameters<typeof BudgetsService.submitBudgetApplication>[0]
  >
) {
  const { mutateAsync: submitBudgetApplication, ...rest } = useMutation({
    mutationFn: BudgetsService.submitBudgetApplication,
    ...options,
  });
  return { submitBudgetApplication, ...rest };
}

export function useSubmitChildcareVoucher(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof BudgetsService.submitChildcareVoucher>>,
    any,
    WithAxiosConfig<Parameters<typeof BudgetsService.submitChildcareVoucher>[0]>
  >
) {
  const { mutateAsync: submitChildcareVoucher, ...rest } = useMutation({
    mutationFn: ({ axiosConfig, ...params }) =>
      BudgetsService.submitChildcareVoucher(params, axiosConfig),
    ...options,
  });
  return { submitChildcareVoucher, ...rest };
}

export function useSetBudgetFamilyMember(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof BudgetsService.setBudgetFamilyMember>>,
    any,
    Parameters<typeof BudgetsService.setBudgetFamilyMember>[0]
  >
) {
  const { mutateAsync: setBudgetFamilyMember, ...rest } = useMutation({
    mutationFn: BudgetsService.setBudgetFamilyMember,
    ...options,
  });
  return { setBudgetFamilyMember, ...rest };
}

export function useCancelBudgetApplication(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof BudgetsService.cancelBudgetApplication>>,
    any,
    Parameters<typeof BudgetsService.cancelBudgetApplication>[0]
  >
) {
  const { mutateAsync: cancelBudgetApplication, ...rest } = useMutation({
    mutationFn: BudgetsService.cancelBudgetApplication,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.DETAIL,
      ]);
    },
    ...options,
  });
  return { cancelBudgetApplication, ...rest };
}

export const useGetBudgetEmployeeExpense = (
  {
    budgetId,
    budgetEmployeeId,
    expenseId,
  }: Parameters<typeof BudgetsService.getBudgetEmployeeExpense>[0],
  options?: UseQueryOptions<IBudgetExpense, unknown, IBudgetExpense, QueryKey>
) => {
  const { data: budgetEmployeeExpense, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_EMPLOYEES.EXPENSES.DETAIL,
      budgetId,
      budgetEmployeeId,
      expenseId,
    ],
    queryFn: () =>
      BudgetsService.getBudgetEmployeeExpense({
        budgetId,
        budgetEmployeeId,
        expenseId,
      }),
    ...options,
  });

  return { budgetEmployeeExpense, ...rest };
};

export const useGetApplicableBudgets = (
  options?: UseQueryOptions<
    IApplicableBudget[],
    unknown,
    IApplicableBudget[],
    QueryKey
  >
) => {
  const { data: applicableBudgets, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT, 'applicable'],
    queryFn: BudgetsService.getApplicableBudgets,
    ...options,
  });

  return { applicableBudgets, ...rest };
};

export const budgetsSummaryQueryOptions = {
  queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT, 'summary'],
  queryFn: BudgetsService.getBudgetsSummary,
};

export function useBudgetsSummary() {
  const { data, error } = useQuery(budgetsSummaryQueryOptions);

  const totalResults = data?.totalResults ?? 0;
  const numberOfActiveBudgets = data?.activeSummary.numResults ?? 0;
  const availableBalance = data?.activeSummary.availableBalance ?? defaultMoney;

  return {
    numberOfBudgets: totalResults,
    numberOfActiveBudgets,
    availableBalance,
    budgetsAvailable: !!data && !error && totalResults > 0,
    loading: !data && !error,
    error,
  };
}
